"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "motion/react";
import Container from "@/components/ui/Container";
import Button from "@/components/ui/Button";
import DeployButton from "@/components/ui/DeployButton";
import { gsap, useGSAP } from "@/lib/gsap";
import TextReveal from "@/components/ui/TextReveal";
import TypeWriter from "@/components/ui/TypeWriter";

/* ── Terminal sequence ───────────────────────────────────────────────────────── */
type TokenType =
  | "prompt"   // ❯ glyph
  | "cmd"      // top-level command (instanodes)
  | "sub"      // subcommand (deploy)
  | "flag"     // --chain, --type
  | "value"    // ethereum, archive
  | "comment"  // # …
  | "info"     // dim diagnostic text
  | "ok"       // ✓ success
  | "url"      // hyperlink-styled
  | "accent"   // ⚡ ready, attention
  | "muted";   // arrow continuations

type Token = { type: TokenType; text: string };

type Line =
  | { kind: "input"; tokens: Token[]; delay: number }
  | { kind: "output"; tokens: Token[]; delay: number; group?: boolean };

const LINES: Line[] = [
  {
    kind: "input",
    delay: 300,
    tokens: [
      { type: "cmd",   text: "instanodes" },
      { type: "sub",   text: " deploy" },
      { type: "flag",  text: " --chain" },
      { type: "value", text: " ethereum" },
      { type: "flag",  text: " --type" },
      { type: "value", text: " archive" },
      { type: "flag",  text: " --region" },
      { type: "value", text: " us-east-1" },
    ],
  },
  {
    kind: "output",
    delay: 900,
    tokens: [
      { type: "muted", text: "→ " },
      { type: "info",  text: "Resolving cluster · spawning 3 replicas…" },
    ],
  },
  {
    kind: "output",
    delay: 1700,
    tokens: [
      { type: "muted", text: "→ " },
      { type: "info",  text: "Health checks passing · TLS provisioned" },
    ],
  },
  {
    kind: "output",
    delay: 2500,
    tokens: [
      { type: "ok",    text: "✓ " },
      { type: "info",  text: "Sync started · block " },
      { type: "value", text: "19,847,322" },
    ],
  },
  {
    kind: "output",
    delay: 3200,
    group: true,
    tokens: [
      { type: "ok",    text: "✓ " },
      { type: "info",  text: "RPC Endpoint " },
      { type: "muted", text: "→ " },
      { type: "url",   text: "https://eth.instanodes.io/v1/abc…xyz" },
    ],
  },
  {
    kind: "output",
    delay: 4000,
    tokens: [
      { type: "accent", text: "⚡ " },
      { type: "info",   text: "Ready in " },
      { type: "value",  text: "4.8s" },
    ],
  },
];

const TOKEN_CLASS: Record<TokenType, string> = {
  prompt:  "text-[#9BE16E]",                   // zsh-prompt green
  cmd:     "text-[#E8E8EC] font-medium",       // primary text
  sub:     "text-[#E8E8EC]",
  flag:    "text-[#B79CFF]",                   // soft purple
  value:   "text-[#FFC36B]",                   // amber
  comment: "text-[#5A5A66]",
  info:    "text-[#A8A8B2]",
  ok:      "text-[#5BD49C]",
  url:     "text-[#7FD7FF] underline decoration-[#7FD7FF]/30 underline-offset-[3px]",
  accent:  "text-[#D58CFF]",
  muted:   "text-[#5A5A66]",
};

function TerminalTokens({ tokens }: { tokens: Token[] }) {
  return (
    <>
      {tokens.map((t, i) => (
        <span key={i} className={TOKEN_CLASS[t.type]}>
          {t.text}
        </span>
      ))}
    </>
  );
}

function Terminal({ started, onComplete }: { started: boolean; onComplete: () => void }) {
  const [visibleLines, setVisibleLines] = useState<number[]>([]);

  useEffect(() => {
    if (!started) return;
    const reset = setTimeout(() => setVisibleLines([]), 0);
    const timers = LINES.map((line, i) =>
      setTimeout(() => {
        setVisibleLines((prev) => [...prev, i]);
        if (i === LINES.length - 1) onComplete();
      }, line.delay + 1),
    );
    return () => { clearTimeout(reset); timers.forEach(clearTimeout); };
  }, [started, onComplete]);

  return (
    <div
      className="cta-terminal relative overflow-hidden rounded-xl border border-white/[0.08] transition-all duration-300"
      style={{
        perspective: 1000,
        background:
          "linear-gradient(180deg, #15151a 0%, #0e0e12 100%)",
        boxShadow:
          "0 14px 44px color-mix(in oklab, var(--accent) 16%, transparent), 0 1px 0 rgba(255,255,255,0.04) inset, 0 0 0 1px rgba(255,255,255,0.02)",
      }}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        e.currentTarget.style.transform = `rotateY(${x * 3}deg) rotateX(${-y * 3}deg)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
      }}
    >
      {/* Top chrome — traffic lights + centered title + live indicator */}
      <div
        className="relative flex h-10 items-center border-b border-white/[0.06] px-4"
        style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0.005))" }}
      >
        {/* Traffic lights */}
        <div className="flex items-center gap-[7px]">
          <span
            aria-hidden
            className="h-3 w-3 rounded-full"
            style={{ background: "#FF5F57", boxShadow: "inset 0 0 0 0.5px rgba(0,0,0,0.25)" }}
          />
          <span
            aria-hidden
            className="h-3 w-3 rounded-full"
            style={{ background: "#FEBC2E", boxShadow: "inset 0 0 0 0.5px rgba(0,0,0,0.25)" }}
          />
          <span
            aria-hidden
            className="h-3 w-3 rounded-full"
            style={{ background: "#28C840", boxShadow: "inset 0 0 0 0.5px rgba(0,0,0,0.25)" }}
          />
        </div>

        {/* Centered title */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2">
          <span
            aria-hidden
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: "color-mix(in oklab, var(--accent) 70%, white)" }}
          />
          <span className="font-mono text-[12px] font-medium tracking-[0.02em] text-white/80">
            instanodes-cli
          </span>
          <span className="font-mono text-[11px] text-white/35">— zsh — 88×24</span>
        </div>

        {/* Live indicator */}
        <div className="ml-auto flex items-center gap-1.5">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#5BD49C] opacity-50" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#5BD49C]" />
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
            live
          </span>
        </div>
      </div>

      {/* Path strip */}
      <div className="flex items-center gap-2 border-b border-white/[0.04] bg-white/[0.015] px-5 py-1.5 font-mono text-[11px]">
        <span className="text-white/30">~/instanodes</span>
        <span className="text-white/20">›</span>
        <span className="text-white/45">deploy</span>
        <span className="text-white/20">·</span>
        <span className="text-white/30">main</span>
        <span className="ml-auto text-[#5BD49C]/70">●</span>
        <span className="text-white/35">node 20.11</span>
      </div>

      {/* Body */}
      <div className="relative min-h-[220px] py-4 pl-2 pr-5 font-mono text-[13px] leading-[1.7]">
        {LINES.map((line, i) => {
          const visible = visibleLines.includes(i);
          const isLast = i === LINES.length - 1;
          return (
            <div
              key={i}
              className="flex items-baseline gap-3 transition-all duration-300 ease-out"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(3px)",
              }}
            >
              {/* Gutter — line number */}
              <span className="w-7 shrink-0 select-none text-right text-[11px] tabular-nums text-white/15">
                {String(i + 1).padStart(2, "0")}
              </span>

              {/* Prompt glyph */}
              <span
                aria-hidden
                className={`shrink-0 ${
                  line.kind === "input" ? "text-[#9BE16E]" : "text-transparent"
                }`}
              >
                ❯
              </span>

              {/* Tokens */}
              <span className="min-w-0 flex-1 break-all">
                <TerminalTokens tokens={line.tokens} />
                {isLast && visible && <span className="cursor-blink ml-1" />}
              </span>
            </div>
          );
        })}

        {/* Idle placeholder */}
        {visibleLines.length === 0 && (
          <div className="flex items-baseline gap-3 text-white/40">
            <span className="w-7 shrink-0 select-none text-right text-[11px] text-white/15">01</span>
            <span className="text-[#9BE16E]" aria-hidden>❯</span>
            <span className="cursor-blink" />
          </div>
        )}

        {/* Faint left guide line — gives output rows a visual rail */}
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-3 left-[80px] top-3 w-px"
          style={{ background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.05), transparent)" }}
        />
      </div>

      {/* Status bar */}
      <div
        className="flex items-center gap-4 border-t border-white/[0.05] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-white/40"
        style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.01), rgba(255,255,255,0))" }}
      >
        <span className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#5BD49C]" />
          connected
        </span>
        <span className="text-white/25">·</span>
        <span>us-east-1</span>
        <span className="text-white/25">·</span>
        <span className="tabular-nums">12 ms</span>
        <span className="ml-auto flex items-center gap-1.5">
          <span>v2.4.1</span>
          <span className="text-white/25">·</span>
          <span>main</span>
        </span>
      </div>
    </div>
  );
}

export default function DeveloperCtaBand() {
  const sectionRef = useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.3 });
  const [started, setStarted] = useState(false);
  const [demoFinished, setDemoFinished] = useState(false);

  const handleComplete = useCallback(() => setDemoFinished(true), []);

  // Auto-start the terminal once in view
  useEffect(() => {
    if (inView && !started) {
      const t = setTimeout(() => setStarted(true), 400);
      return () => clearTimeout(t);
    }
  }, [inView, started]);

  // ── GSAP: Section entrance + CTA spring-in ────────────────────────────────
  useGSAP(
    () => {
      if (!sectionRef.current) return;

      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        // Set initial hidden states
        gsap.set(".cta-headline, .cta-sub, .cta-terminal, .cta-buttons, .cta-trust-item", {
          autoAlpha: 0,
          y: 30,
        });

        // Headline entrance
        gsap.to(".cta-headline", {
          autoAlpha: 1,
          y: 0,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 85%",
          },
        });

        // Subtitle
        gsap.to(".cta-sub", {
          autoAlpha: 1,
          y: 0,
          duration: 0.6,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
          },
        });

        // Terminal slides up with scale
        gsap.set(".cta-terminal", { scale: 0.97 });
        gsap.to(".cta-terminal", {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".cta-terminal",
            start: "top 90%",
          },
        });

        // CTAs spring in
        gsap.to(".cta-buttons", {
          autoAlpha: 1,
          y: 0,
          duration: 0.5,
          ease: "back.out(1.7)",
          scrollTrigger: {
            trigger: ".cta-buttons",
            start: "top 92%",
          },
        });

        // Trust row items stagger from center
        gsap.to(".cta-trust-item", {
          autoAlpha: 1,
          y: 0,
          stagger: {
            each: 0.06,
            from: "center",
          },
          duration: 0.4,
          scrollTrigger: {
            trigger: ".cta-trust-row",
            start: "top 95%",
          },
        });

        // Background glow intensifies with scroll
        gsap.fromTo(
          ".cta-glow",
          { opacity: 0.2 },
          {
            opacity: 0.8,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 60%",
              end: "bottom 40%",
              scrub: 1,
            },
          },
        );

        // Dot grid parallax
        gsap.to(".cta-dot-grid", {
          y: -40,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });
      });
    },
    { scope: sectionRef },
  );

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden py-16"
    >
      {/* Background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 bg-bg-base" />
      <div aria-hidden className="cta-glow bg-glow-dual pointer-events-none absolute inset-0 -z-10" />
      <div aria-hidden className="cta-dot-grid bg-dot-grid pointer-events-none absolute inset-0 -z-10 opacity-40" />
      {/* Edge fade */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-32 bg-gradient-to-b from-bg-surface to-transparent" />

      <Container size="narrow">
        {/* Headline */}
        <TextReveal tag="h2" className="cta-headline mb-4 text-center text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl">
          Deploy your first node{" "}
          <span className="gradient-text-cyan">
            in under 5 minutes.
          </span>
        </TextReveal>

        <p className="cta-sub mx-auto mb-12 max-w-lg text-center text-lg font-light leading-[1.7] text-text-secondary">
          <TypeWriter
            text="Pick a chain, get an RPC Endpoint. Works with ethers.js, viem, wagmi, and any JSON-RPC client."
            speed={0.025}
            delay={0.3}
          />
        </p>

        {/* Terminal */}
        <div className="mb-10">
          <Terminal started={started} onComplete={handleComplete} />
          {demoFinished && (
            <button
              onClick={() => { setDemoFinished(false); setStarted(false); setTimeout(() => setStarted(true), 50); }}
              className="focus-ring mx-auto mt-4 flex items-center gap-1.5 rounded-full border border-border-medium px-4 py-2 text-xs font-medium text-text-secondary transition-colors hover:border-brand-cyan/40 hover:text-white"
            >
              ↺ Replay demo
            </button>
          )}
        </div>

        {/* CTAs */}
        <div className="cta-buttons flex flex-wrap items-center justify-center gap-4">
          <DeployButton
            size="lg"
            location="cta-band"
            label="Deploy Your Node in Under 5 Minutes"
          />
          <Button
            href="/contact-us"
            variant="outline"
            size="lg"
            data-cta="talk-to-experts"
            data-cta-location="cta-band"
          >
            Talk to Our Experts
          </Button>
        </div>

        {/* Trust row */}
        <div className="cta-trust-row mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-text-muted">
          {["No credit card to start", "99.95% uptime SLA", "Cancel anytime", "Public status page"].map((t) => (
            <span key={t} className="cta-trust-item flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-brand-cyan opacity-60" />
              {t}
            </span>
          ))}
        </div>
      </Container>
    </section>
  );
}
